// FILE: src/app/features/calendar/appointment-history/appointment-history.component.ts
// ACTION: CREATE new file

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';

export interface AppointmentHistoryItem {
  id: string;
  appointment_from: string;
  appointment_to: string;
  consultation_fee: string;
  total_amount: string;
  amount_paid: string;
  appointment_status: string;
  payment_status: string;
  payment_method: string;
  lawyer_name: string;
  lawyer_profile_pic: string;
  specialization: string[];
  created_at: string;
}

export interface AppointmentHistoryResponse {
  error: boolean;
  appointments: AppointmentHistoryItem[];
}

@Component({
    selector: 'app-appointment-history',
    templateUrl: './appointment-history.component.html',
    styleUrls: ['./appointment-history.component.css'],
    standalone: false
})
export class AppointmentHistoryComponent implements OnInit {

  isLoading = true;
  historyItems: AppointmentHistoryItem[] = [];
  lawyerId = '';
  lawyerName = '';
  lawyerProfilePic = '';
  lawyerInitials = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private toastr: ToastrService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    const state = history.state as {
      lawyerId?: string;
      lawyerName?: string;
      lawyerProfilePic?: string;
    };

    // Primary path: lawyer_id arrives via router state from goAppointmentHistory().
    // Fallback to query/route params so this page still resolves correctly
    // if it's ever deep-linked or refreshed (state is lost on refresh).
    this.lawyerId = state?.lawyerId
      || this.route.snapshot.queryParamMap.get('lawyer_id')
      || this.route.snapshot.paramMap.get('lawyer_id')
      || '';

    this.lawyerName = state?.lawyerName || '';
    this.lawyerProfilePic = state?.lawyerProfilePic || '';
    this.lawyerInitials = this.getInitials(this.lawyerName);

    if (!this.lawyerId) {
      // The history API requires lawyer_id — without it there's nothing to fetch.
      this.isLoading = false;
      this.toastr.error('Unable to load history — lawyer information is missing.');
      return;
    }

    this.loadHistory();
  }

  loadHistory(): void {
    this.isLoading = true;
    this.auth.getAppointmentsLawyerHistory(this.lawyerId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.error === false) {
          // Sort by appointment_from DESC (latest first)
          const sorted = (res.appointments || []).sort((a: AppointmentHistoryItem, b: AppointmentHistoryItem) => {
            return new Date(b.appointment_from).getTime() - new Date(a.appointment_from).getTime();
          });
          this.historyItems = sorted;

          // If name wasn't passed via state, derive from first record
          if (!this.lawyerName && sorted.length > 0) {
            this.lawyerName = sorted[0].lawyer_name || '';
            this.lawyerProfilePic = sorted[0].lawyer_profile_pic || '';
            this.lawyerInitials = this.getInitials(this.lawyerName);
          }
        } else {
          this.historyItems = [];
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        const msg = err?.error?.msg || 'Failed to load appointment history.';
        this.toastr.error(msg);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/consumer/appointments']);
  }

  getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  formatDateRange(from: string, to: string): string {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const datePart = this.datePipe.transform(fromDate, 'MMM dd, yyyy') || '';
    const fromTime = this.datePipe.transform(fromDate, 'hh:mm a') || '';
    const toTime = this.datePipe.transform(toDate, 'hh:mm a') || '';
    return `${datePart} | ${fromTime} - ${toTime}`;
  }

  getPaymentAmount(item: AppointmentHistoryItem): string {
    const status = (item.appointment_status || '').toLowerCase();
    const paymentStatus = (item.payment_status || '').toLowerCase();

    if (
      (status === 'cancelled' || status === 'rejected') &&
      (paymentStatus === 'refunded' || paymentStatus === 'refund' || paymentStatus === 'refund_initiated')
    ) {
      return item.consultation_fee || '0';
    }

    return item.amount_paid || item.total_amount || item.consultation_fee || '0';
  }

  // For cancelled appointments, swap the generic "Payment:" label based on
  // payment_status so the user can tell whether the refund is still in
  // progress or has been completed.
  getPaymentLabel(item: AppointmentHistoryItem): string {
    const status = (item.appointment_status || '').toLowerCase();
    const paymentStatus = (item.payment_status || '').toLowerCase();

    if (status === 'cancelled' || status === 'rejected') {
      if (paymentStatus === 'refund_initiated') {
        return 'Refund Initiated Payment';
      }
      if (paymentStatus === 'refund' || paymentStatus === 'refunded') {
        return 'Refunded Payment';
      }
    }

    return 'Payment';
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return 'completed';
    if (s === 'confirmed') return 'confirmed';
    if (s === 'cancelled' || s === 'rejected') return 'cancelled';
    if (s === 'upcoming') return 'upcoming';
    if (s === 'ongoing') return 'ongoing';
    return 'pending';
  }

  getStatusLabel(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return 'Completed';
    if (s === 'confirmed') return 'Confirmed';
    if (s === 'cancelled' || s === 'rejected') return 'Cancelled';
    if (s === 'upcoming') return 'Upcoming';
    if (s === 'ongoing') return 'Ongoing';
    return 'Pending';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}